// src/components/Pedagogy/PedagogyLibrary.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import Layout from '../Layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'

const {
  FiTag,
  FiFilter,
  FiBookOpen,
  FiArchive,
  FiHeart,
  FiSlash,
} = FiIcons

const pedagogyTypeLabels = {
  general: 'General Strategy',
  thinking_routine: 'Thinking Routine',
  assessment: 'Assessment Strategy',
  differentiation: 'Differentiation',
  classroom_management: 'Classroom Management',
  subject_specific: 'Subject-Specific Strategy',
  engagement: 'Engagement Strategy',
}

const PREF_FILTERS = {
  all: 'All',
  favourites: 'Favourites',
  blocked: "Don't Use",
}

const prettyType = (type) =>
  pedagogyTypeLabels[type] ||
  String(type || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const PedagogyLibrary = () => {
  const { userProfile } = useAuth()
  const classroomUserId = userProfile?.id ?? null

  const [items, setItems] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeItem, setActiveItem] = useState(null)

  // Primary filter (type)
  const [selectedTypes, setSelectedTypes] = useState([])

  // Optional filter (tags)
  const [showTagFilters, setShowTagFilters] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])

  // Preference quick filter
  const [prefFilter, setPrefFilter] = useState('all') // all | favourites | blocked

  // Preferences map: { [pedagogy_document_id]: { is_favourite, is_blocked } }
  const [prefsByDocId, setPrefsByDocId] = useState({})

  useEffect(() => {
    loadDocuments()
  }, [])

  useEffect(() => {
    if (classroomUserId) {
      loadPreferences(classroomUserId)
    } else {
      setPrefsByDocId({})
    }
  }, [classroomUserId])

  const loadDocuments = async () => {
    setLoading(true)

    const { data, error } = await supabase
      .schema('classroom')
      .from('pedagogy_documents')
      .select('*')
      .order('title', { ascending: true })

    if (error) {
      console.error('Error loading pedagogy library:', error)
      setItems([])
      setFiltered([])
      setLoading(false)
      return
    }

    setItems(data || [])
    setFiltered(data || [])
    setLoading(false)
  }

  const loadPreferences = async (uid) => {
    const { data, error } = await supabase
      .schema('classroom')
      .from('pedagogy_user_preferences')
      .select('pedagogy_document_id,is_favourite,is_blocked')
      .eq('user_id', uid)

    if (error) {
      console.error('Error loading pedagogy preferences:', error)
      setPrefsByDocId({})
      return
    }

    const map = {}
    for (const row of data || []) {
      map[row.pedagogy_document_id] = {
        is_favourite: !!row.is_favourite,
        is_blocked: !!row.is_blocked,
      }
    }
    setPrefsByDocId(map)
  }

  const getPref = (docId) => {
    return prefsByDocId[docId] || { is_favourite: false, is_blocked: false }
  }

  // Available type badges derived from actual DB values
  const availableTypes = useMemo(() => {
    const s = new Set()
    for (const i of items) {
      if (i?.pedagogy_type) s.add(i.pedagogy_type)
    }
    return Array.from(s).sort((a, b) => prettyType(a).localeCompare(prettyType(b)))
  }, [items])

  // Available tags (only used when showTagFilters is open)
  const availableTags = useMemo(() => {
    const s = new Set()
    for (const i of items) {
      const tags = Array.isArray(i.tags) ? i.tags : []
      for (const t of tags) {
        const cleaned = String(t || '').trim()
        if (cleaned) s.add(cleaned)
      }
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [items])

  const applyFilters = (types, tags, preferenceFilter) => {
    let results = items

    // Type filter: match ANY selected type
    if (types.length > 0) {
      const selected = new Set(types)
      results = results.filter((i) => selected.has(i.pedagogy_type))
    }

    // Tag filter: match ANY selected tag
    if (tags.length > 0) {
      const selected = new Set(tags.map((t) => String(t).toLowerCase()))
      results = results.filter((i) => {
        const itemTags = (Array.isArray(i.tags) ? i.tags : []).map((t) =>
          String(t || '').toLowerCase()
        )
        return itemTags.some((t) => selected.has(t))
      })
    }

    // Preference quick filter
    if (preferenceFilter === 'favourites') {
      results = results.filter((i) => getPref(i.id).is_favourite)
    } else if (preferenceFilter === 'blocked') {
      results = results.filter((i) => getPref(i.id).is_blocked)
    }

    setFiltered(results)
  }

  useEffect(() => {
    applyFilters(selectedTypes, selectedTags, prefFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, selectedTags, prefFilter, items, prefsByDocId])

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const formatArray = (arr) => {
    if (!arr || arr.length === 0) return null
    return arr.join(', ')
  }

  /**
   * Upsert preference row for (user_id, pedagogy_document_id)
   * - Mutually exclusive enforced in UI (and by DB CHECK constraint).
   */
  const setPreference = async (docId, next) => {
    if (!classroomUserId) {
      console.warn(
        'User must be signed in / have a userProfile to set preferences.'
      )
      return
    }

    const payload = {
      user_id: classroomUserId,
      pedagogy_document_id: docId,
      is_favourite: !!next.is_favourite && !next.is_blocked,
      is_blocked: !!next.is_blocked,
    }

    // Optimistic update
    setPrefsByDocId((prev) => ({
      ...prev,
      [docId]: {
        is_favourite: payload.is_favourite,
        is_blocked: payload.is_blocked,
      },
    }))

    const { error } = await supabase
      .schema('classroom')
      .from('pedagogy_user_preferences')
      .upsert(payload, { onConflict: 'user_id,pedagogy_document_id' })

    if (error) {
      console.error('Error saving preference:', error)
      loadPreferences(classroomUserId)
    }
  }

  const toggleFavourite = (docId) => {
    const cur = getPref(docId)
    const nextFav = !cur.is_favourite
    setPreference(docId, { is_favourite: nextFav, is_blocked: false })
  }

  const toggleBlocked = (docId) => {
    const cur = getPref(docId)
    const nextBlocked = !cur.is_blocked
    setPreference(docId, { is_favourite: false, is_blocked: nextBlocked })
  }

  const PreferenceButtons = ({ itemId, compact = false }) => {
    const pref = getPref(itemId)

    const favClasses = pref.is_favourite
      ? 'bg-pink-600 text-white border-pink-600'
      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'

    const blockClasses = pref.is_blocked
      ? 'bg-gray-900 text-white border-gray-900'
      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'

    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            toggleFavourite(itemId)
          }}
          className={[
            'inline-flex items-center rounded-full text-xs font-medium border transition-colors',
            compact ? 'px-2 py-1' : 'px-3 py-1',
            favClasses,
            pref.is_blocked ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
          title="Favourite"
          disabled={pref.is_blocked}
        >
          <SafeIcon icon={FiHeart} className="w-3 h-3 mr-1" />
          Favourite
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            toggleBlocked(itemId)
          }}
          className={[
            'inline-flex items-center rounded-full text-xs font-medium border transition-colors',
            compact ? 'px-2 py-1' : 'px-3 py-1',
            blockClasses,
          ].join(' ')}
          title="Don't Use"
        >
          <SafeIcon icon={FiSlash} className="w-3 h-3 mr-1" />
          Don&apos;t Use
        </button>
      </div>
    )
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pedagogy Library</h1>
          <p className="text-gray-600 mt-1">
            A curated collection of teaching strategies and routines used to
            ground all generated lesson plans in solid pedagogy.
          </p>
        </div>

        {/* Quick preference filter */}
        <div className="flex items-center gap-2">
          {Object.entries(PREF_FILTERS).map(([key, label]) => {
            const active = prefFilter === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setPrefFilter(key)}
                className={[
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                ].join(' ')}
              >
                {label}
              </button>
            )
          })}
        </div>

        {/* Type Filters (primary) */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
            <span>Filter by type</span>

            {selectedTypes.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedTypes([])}
                className="ml-2 text-xs text-blue-600 hover:text-blue-700"
              >
                Clear
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowTagFilters((v) => !v)}
              className="ml-auto text-xs text-gray-600 hover:text-gray-800"
            >
              {showTagFilters ? 'Hide tag filters' : 'Show tag filters'}
            </button>
          </div>

          {availableTypes.length === 0 ? (
            <div className="text-sm text-gray-500">
              No pedagogy types found yet.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableTypes.map((type) => {
                const active = selectedTypes.includes(type)
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleType(type)}
                    className={[
                      'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                    ].join(' ')}
                    title={type}
                  >
                    {prettyType(type)}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Tag Filters (optional / collapsible) */}
        {showTagFilters && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
              <span>Filter by tag</span>
              {selectedTags.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedTags([])}
                  className="ml-2 text-xs text-blue-600 hover:text-blue-700"
                >
                  Clear
                </button>
              )}
            </div>

            {availableTags.length === 0 ? (
              <div className="text-sm text-gray-500">
                No tags found yet. Add tags to pedagogy documents to enable
                filtering.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const active = selectedTags.includes(tag)
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={[
                        'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                      ].join(' ')}
                      title={tag}
                    >
                      <SafeIcon
                        icon={FiTag}
                        className={[
                          'w-3 h-3 mr-1',
                          active ? 'text-white' : 'text-gray-500',
                        ].join(' ')}
                      />
                      {tag}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Items List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">
            Loading pedagogy library…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 space-y-3">
            <SafeIcon icon={FiArchive} className="w-8 h-8 text-gray-400" />
            <p>No pedagogy documents found.</p>
            <p className="text-sm text-gray-400">
              Try clearing filters or selecting different filters.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((item) => {
              const pref = getPref(item.id)
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveItem(item)}
                  className={[
                    'text-left p-6 border rounded-lg bg-white shadow-sm transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500',
                    pref.is_blocked
                      ? 'opacity-60 grayscale hover:shadow-sm'
                      : 'hover:shadow-md',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                          {prettyType(item.pedagogy_type)}
                        </span>

                        {pref.is_blocked && (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                            Don&apos;t Use
                          </span>
                        )}
                        {pref.is_favourite && !pref.is_blocked && (
                          <span className="text-xs px-2 py-1 rounded-full bg-pink-50 text-pink-700">
                            Favourite
                          </span>
                        )}
                      </div>
                    </div>

                    <PreferenceButtons itemId={item.id} compact />
                  </div>

                  {item.summary && (
                    <p className="text-gray-600 text-sm mt-3">{item.summary}</p>
                  )}

                  {pref.is_blocked && (
                    <p className="text-xs text-gray-500 mt-2">
                      This strategy is currently blocked and will not be used in lesson generation.
                    </p>
                  )}

                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {item.tags.map((t, i) => (
                        <span
                          key={`${t}-${i}`}
                          className="inline-flex items-center text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full"
                        >
                          <SafeIcon
                            icon={FiTag}
                            className="w-3 h-3 mr-1 text-gray-500"
                          />
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Detail Drawer */}
        {activeItem && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex justify-end z-40"
            onClick={() => setActiveItem(null)}
            role="presentation"
          >
            <div
              className="w-full max-w-md bg-white h-full p-6 overflow-y-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <SafeIcon
                    icon={FiBookOpen}
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="uppercase text-xs font-semibold text-gray-500">
                    Pedagogy detail
                  </span>
                </div>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700 text-sm"
                  onClick={() => setActiveItem(null)}
                >
                  Close
                </button>
              </div>

              {/* Pref actions */}
              <div className="mb-4">
                <PreferenceButtons itemId={activeItem.id} />
                <p className="text-xs text-gray-400 mt-2">
                  Blocked overrides favourite.
                </p>
                {!classroomUserId && (
                  <p className="text-xs text-red-500 mt-2">
                    Sign in to save favourites and blocked items.
                  </p>
                )}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {activeItem.title}
              </h2>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                  {prettyType(activeItem.pedagogy_type)}
                </span>

                {activeItem.subjects?.length > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                    Subjects: {formatArray(activeItem.subjects)}
                  </span>
                )}

                {activeItem.year_levels?.length > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                    Year levels: {formatArray(activeItem.year_levels)}
                  </span>
                )}
              </div>

              {activeItem.summary && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">
                    Summary
                  </h3>
                  <p className="text-sm text-gray-700">{activeItem.summary}</p>
                </div>
              )}

              {activeItem.tags?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {activeItem.tags.map((t, i) => (
                      <span
                        key={`${t}-${i}`}
                        className="inline-flex items-center text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full"
                      >
                        <SafeIcon
                          icon={FiTag}
                          className="w-3 h-3 mr-1 text-gray-500"
                        />
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeItem.raw_text && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">
                    Full description / guidance
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {activeItem.raw_text}
                  </p>
                </div>
              )}

              {!activeItem.raw_text && !activeItem.summary && (
                <div className="mb-4 text-sm text-gray-500">
                  No detailed text stored for this pedagogy item yet.
                </div>
              )}

              {activeItem.source_reference && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">
                    Source / reference
                  </h3>
                  <p className="text-xs text-gray-600">
                    {activeItem.source_reference}
                  </p>
                </div>
              )}

              {/* Placeholder for future actions */}
              <div className="mt-6 flex flex-col gap-2 text-xs text-gray-400">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiArchive} className="w-3 h-3" />
                  <span>
                    Future idea: add &quot;Use in current lesson&quot; button
                    here to attach this strategy to a lesson design.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default PedagogyLibrary